/**
 * nodeReordering.ts - Utilities for reordering nodes in the automation flow
 *
 * Provides functions for:
 * - Moving nodes to different positions in the flow
 * - Analyzing node relationships (parent, children, siblings)
 * - Validating reorder operations
 *
 * Related:
 *   - utils/autoLayout.ts - Re-applies layout after reordering
 *   - components/AutomationFlowCanvas.tsx - Integrates reordering
 */

import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData, SegmentNodeData } from '../types';
import { applyLayout } from './layoutEngine';
import { NODE_TYPES } from '../types';
import { getParentNodeId, getChildNodeIds, getDescendantNodeIds, wouldCreateCycle } from './graphHelpers';
import { isTriggerAutomationStepNode, TRIGGER_AUTOMATION_LAST_STEP_MESSAGE } from './triggerAutomationStepRules';

// Re-export graph helpers for backwards compatibility
export { getParentNodeId, getChildNodeIds, getDescendantNodeIds };

/**
 * Result of a node move operation
 */
interface MoveNodeResult {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	success: boolean;
	message?: string;
}

/**
 * Move a node to be after another node in the flow.
 * This re-wires the edges and re-applies layout.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param nodeId - ID of node to move
 * @param targetNodeId - ID of node to place after
 * @param sourceHandle - Optional source handle for switch nodes
 */
export function moveNodeAfter(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	nodeId: string,
	targetNodeId: string,
	sourceHandle?: string
): MoveNodeResult {
	const nodeToMove = nodes.find(n => n.id === nodeId);
	const targetNode = nodes.find(n => n.id === targetNodeId);

	if (!nodeToMove || !targetNode) {
		return { nodes, edges, success: false, message: 'Node not found' };
	}

	// Can't move trigger nodes
	if (nodeToMove.type === NODE_TYPES.TRIGGER) {
		return { nodes, edges, success: false, message: 'Cannot move trigger nodes' };
	}

	if (isTriggerAutomationStepNode(targetNode)) {
		return { nodes, edges, success: false, message: TRIGGER_AUTOMATION_LAST_STEP_MESSAGE };
	}

	// Check for cycles
	if (wouldCreateCycle(nodeId, targetNodeId, edges)) {
		return { nodes, edges, success: false, message: 'Cannot create a cycle' };
	}

	// Get the node's current parent and children
	const currentParentId = getParentNodeId(nodeId, edges);
	const nodeChildren = getChildNodeIds(nodeId, edges);
	const targetChildren = getChildNodeIds(targetNodeId, edges);
	if (isTriggerAutomationStepNode(nodeToMove) && targetChildren.some(childId => childId !== nodeId)) {
		return { nodes, edges, success: false, message: TRIGGER_AUTOMATION_LAST_STEP_MESSAGE };
	}

	// Create new edge list
	const newEdges = edges.filter(e => {
		// Remove incoming edge to moving node
		if (e.target === nodeId) {
			return false;
		}
		// Remove outgoing edges from moving node (prevents stale edges on repeated moves)
		if (e.source === nodeId) {
			return false;
		}
		// Remove outgoing edge from target node (if connecting to same branch)
		if (e.source === targetNodeId) {
			// Only remove if no sourceHandle or handles match
			if (!sourceHandle || e.sourceHandle === sourceHandle || !e.sourceHandle) {
				return false;
			}
		}
		return true;
	});

	// Add new edge from target to moved node
	newEdges.push({
		id: `edge-${targetNodeId}-${nodeId}`,
		source: targetNodeId,
		target: nodeId,
		sourceHandle,
		type: 'default',
	});

	// Connect the moved node to the target's former child (if any).
	// After removing the moving node's outgoing edges, it always needs to adopt the
	// target's former children to maintain flow continuity.
	if (targetChildren.length > 0) {
		const targetChild = targetChildren[0];
		newEdges.push({
			id: `edge-${nodeId}-${targetChild}`,
			source: nodeId,
			target: targetChild,
			type: 'default',
		});
	}

	// If the node had a parent and that parent lost its only child,
	// we need to reconnect the parent to the node's children
	if (currentParentId && nodeChildren.length > 0) {
		const parentChildren = getChildNodeIds(currentParentId, newEdges);
		if (parentChildren.length === 0) {
			// Connect parent to first child of moved node
			newEdges.push({
				id: `edge-${currentParentId}-${nodeChildren[0]}`,
				source: currentParentId,
				target: nodeChildren[0],
				type: 'default',
			});
		}
	}

	// Re-apply layout
	const layoutResult = applyLayout(nodes, newEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		success: true,
	};
}

/**
 * Move a node to be before another node in the flow.
 * This inserts the node between the target's parent and the target.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param nodeId - ID of node to move
 * @param targetNodeId - ID of node to place before
 */
export function moveNodeBefore(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	nodeId: string,
	targetNodeId: string
): MoveNodeResult {
	const nodeToMove = nodes.find(n => n.id === nodeId);
	const targetNode = nodes.find(n => n.id === targetNodeId);

	if (!nodeToMove || !targetNode) {
		return { nodes, edges, success: false, message: 'Node not found' };
	}

	// Can't move trigger nodes
	if (nodeToMove.type === NODE_TYPES.TRIGGER) {
		return { nodes, edges, success: false, message: 'Cannot move trigger nodes' };
	}

	// Can't move before trigger
	if (targetNode.type === NODE_TYPES.TRIGGER) {
		return { nodes, edges, success: false, message: 'Cannot move before trigger' };
	}

	if (isTriggerAutomationStepNode(nodeToMove)) {
		return { nodes, edges, success: false, message: TRIGGER_AUTOMATION_LAST_STEP_MESSAGE };
	}

	// Check for cycles
	if (wouldCreateCycle(nodeId, targetNodeId, edges)) {
		return { nodes, edges, success: false, message: 'Cannot create a cycle' };
	}

	// Get the target's parent (will become moved node's parent)
	const targetParentId = getParentNodeId(targetNodeId, edges);
	if (!targetParentId) {
		return { nodes, edges, success: false, message: 'Target has no parent' };
	}

	// Get the current parent of moving node
	const currentParentId = getParentNodeId(nodeId, edges);
	const nodeChildren = getChildNodeIds(nodeId, edges);

	// Find the edge connecting target's parent to target (to get sourceHandle)
	const parentToTargetEdge = edges.find(e => e.source === targetParentId && e.target === targetNodeId);
	const sourceHandle = parentToTargetEdge?.sourceHandle;

	// Create new edge list
	const newEdges = edges.filter(e => {
		// Remove incoming edge to moving node
		if (e.target === nodeId) {
			return false;
		}
		// Remove outgoing edges from moving node (prevents stale edges on repeated moves)
		if (e.source === nodeId) {
			return false;
		}
		// Remove incoming edge to target node
		if (e.target === targetNodeId) {
			return false;
		}
		return true;
	});

	// Add edge from target's parent to moved node
	newEdges.push({
		id: `edge-${targetParentId}-${nodeId}`,
		source: targetParentId,
		target: nodeId,
		sourceHandle,
		type: 'default',
	});

	// Add edge from moved node to target
	newEdges.push({
		id: `edge-${nodeId}-${targetNodeId}`,
		source: nodeId,
		target: targetNodeId,
		type: 'default',
	});

	// If the node had a parent and children, reconnect them
	if (currentParentId && nodeChildren.length > 0) {
		// Check if parent lost its only path
		const parentChildrenAfterRemove = newEdges.filter(e => e.source === currentParentId).length;
		if (parentChildrenAfterRemove === 0) {
			// Connect parent to first child of moved node
			newEdges.push({
				id: `edge-${currentParentId}-${nodeChildren[0]}`,
				source: currentParentId,
				target: nodeChildren[0],
				type: 'default',
			});
		}
	}

	// Re-apply layout
	const layoutResult = applyLayout(nodes, newEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		success: true,
	};
}

/**
 * Select all nodes in a branch starting from a given node
 *
 * @param startNodeId - The node to start from
 * @param edges - All edges
 * @returns Array of node IDs in the branch
 */
export function selectBranch(startNodeId: string, edges: Edge[]): string[] {
	const branch: string[] = [startNodeId];
	const descendants = getDescendantNodeIds(startNodeId, edges);
	branch.push(...descendants);
	return branch;
}

/**
 * Get the insertion point info when dragging to a position
 */
export interface InsertionPoint {
	type: 'after' | 'before' | 'invalid';
	targetNodeId: string | null;
	sourceHandle?: string;
}

/**
 * Check if a step node can move up (swap with predecessor).
 *
 * A step can move up if:
 * - It has a predecessor in the flow
 * - The predecessor is not a trigger node
 * - The node itself is not a trigger node
 * - Special case: If predecessor is a non-default segment, step can move to default segment
 * - Special case: If predecessor is a default segment, step cannot move up
 * - Special case: Segments can move up if they have a step predecessor (moves entire switch)
 *
 * @param nodeId - ID of the node to check
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @returns true if the step can move up
 */
export function canMoveStepUp(nodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): boolean {
	const node = nodes.find(n => n.id === nodeId);
	if (!node) {
		return false;
	}

	// Can't move triggers
	if (node.type === NODE_TYPES.TRIGGER) {
		return false;
	}

	if (isTriggerAutomationStepNode(node)) {
		return false;
	}

	// Segments (conditional branches) are not reorderable via move up/down.
	if (node.type === NODE_TYPES.SEGMENT) {
		return false;
	}

	const parentId = getParentNodeId(nodeId, edges);
	if (!parentId) {
		return false;
	}

	const parentNode = nodes.find(n => n.id === parentId);
	if (!parentNode) {
		return false;
	}

	// Can't move up past a trigger
	if (parentNode.type === NODE_TYPES.TRIGGER) {
		return false;
	}

	// Special case: If parent is a segment
	if (parentNode.type === NODE_TYPES.SEGMENT) {
		const segmentData = parentNode.data as SegmentNodeData;
		// Can't move up if already under default segment (nowhere to go)
		if (segmentData.isDefault) {
			return false;
		}
		// Non-default segment: can move to default segment
		return true;
	}

	return true;
}

/**
 * Check if a step node can move down (swap with successor).
 *
 * A step can move down if:
 * - It has exactly one child that is a step node (not segment/trigger)
 * - The node itself is not a trigger node
 * - Special case: Segments can move down if there's a step under the default segment
 *
 * @param nodeId - ID of the node to check
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @returns true if the step can move down
 */
export function canMoveStepDown(nodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): boolean {
	const node = nodes.find(n => n.id === nodeId);
	if (!node) {
		return false;
	}

	// Can't move triggers
	if (node.type === NODE_TYPES.TRIGGER) {
		return false;
	}

	if (isTriggerAutomationStepNode(node)) {
		return false;
	}

	// Segments (conditional branches) are not reorderable via move up/down.
	if (node.type === NODE_TYPES.SEGMENT) {
		return false;
	}

	const childIds = getChildNodeIds(nodeId, edges);

	// Must have exactly one child for simple swap
	if (childIds.length !== 1) {
		return false;
	}

	const childNode = nodes.find(n => n.id === childIds[0]);
	if (!childNode) {
		return false;
	}

	// Can't move down if successor is a segment, trigger, or terminal Start Automation step.
	if (
		childNode.type === NODE_TYPES.SEGMENT ||
		childNode.type === NODE_TYPES.TRIGGER ||
		isTriggerAutomationStepNode(childNode)
	) {
		return false;
	}

	return true;
}

/**
 * Move a step up by swapping with its predecessor.
 *
 * The step takes the predecessor's position in the flow, and the predecessor
 * becomes the step's successor.
 *
 * Special case: If the step is directly under a non-default segment, it moves
 * to become the last step under the default segment of the same switch node.
 *
 * Special case: If moving a segment, the entire switch structure moves up by
 * relocating the step above the switch into the default segment.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param nodeId - ID of the node to move up
 * @returns MoveNodeResult with updated nodes and edges
 */
export function moveStepUp(nodes: Node<BaseNodeData>[], edges: Edge[], nodeId: string): MoveNodeResult {
	if (!canMoveStepUp(nodeId, nodes, edges)) {
		return { nodes, edges, success: false, message: 'Cannot move step up' };
	}

	const node = nodes.find(n => n.id === nodeId);
	if (!node) {
		return { nodes, edges, success: false, message: 'Node not found' };
	}

	// Special case: Moving a segment moves the entire switch structure up
	if (node.type === NODE_TYPES.SEGMENT) {
		return moveSegmentUp(nodes, edges, nodeId);
	}

	const predecessorId = getParentNodeId(nodeId, edges);
	if (!predecessorId) {
		return { nodes, edges, success: false, message: 'No predecessor found' };
	}

	const predecessorNode = nodes.find(n => n.id === predecessorId);
	if (!predecessorNode) {
		return { nodes, edges, success: false, message: 'Predecessor not found' };
	}

	// Special case: If predecessor is a non-default segment, move to default segment
	if (predecessorNode.type === NODE_TYPES.SEGMENT) {
		const segmentData = predecessorNode.data as SegmentNodeData;

		// Find the default segment for the same switch node
		const defaultSegment = nodes.find(
			n =>
				n.type === NODE_TYPES.SEGMENT &&
				(n.data as SegmentNodeData).parentSwitchNodeId === segmentData.parentSwitchNodeId &&
				(n.data as SegmentNodeData).isDefault
		);

		if (!defaultSegment) {
			return { nodes, edges, success: false, message: 'Default segment not found' };
		}

		// Find the last step under the default segment
		const targetNodeId = findLastStepInSegment(defaultSegment.id, nodes, edges);

		// Move the step to be after the target (last step in default segment, or the segment itself)
		return moveNodeAfter(nodes, edges, nodeId, targetNodeId);
	}

	// Normal case: Move this node to be before the predecessor
	return moveNodeBefore(nodes, edges, nodeId, predecessorId);
}

/**
 * Move a segment (and its entire switch structure) up by one position.
 *
 * This works by moving the step that's above the switch into the default segment.
 * The switch effectively moves up because it's now connected to whatever was
 * above that step.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param segmentId - ID of the segment node
 * @returns MoveNodeResult with updated nodes and edges
 */
function moveSegmentUp(nodes: Node<BaseNodeData>[], edges: Edge[], segmentId: string): MoveNodeResult {
	const segmentNode = nodes.find(n => n.id === segmentId);
	if (!segmentNode || segmentNode.type !== NODE_TYPES.SEGMENT) {
		return { nodes, edges, success: false, message: 'Segment not found' };
	}

	const segmentData = segmentNode.data as SegmentNodeData;

	// Find all segments for this switch (we'll need to rewire them all)
	const allSegments = nodes.filter(
		n =>
			n.type === NODE_TYPES.SEGMENT && (n.data as SegmentNodeData).parentSwitchNodeId === segmentData.parentSwitchNodeId
	);

	// Find the visual source (step that connects to segments)
	const incomingEdge = edges.find(e => e.target === segmentId);
	if (!incomingEdge) {
		return { nodes, edges, success: false, message: 'No incoming edge found' };
	}

	const visualSourceId = incomingEdge.source;
	const visualSourceNode = nodes.find(n => n.id === visualSourceId);
	if (!visualSourceNode) {
		return { nodes, edges, success: false, message: 'Visual source not found' };
	}

	// Find what's above the visual source (this will become the new visual source)
	// This can be a trigger or another step
	const newVisualSourceId = getParentNodeId(visualSourceId, edges);
	if (!newVisualSourceId) {
		return { nodes, edges, success: false, message: 'No parent found for visual source' };
	}

	const newVisualSourceNode = nodes.find(n => n.id === newVisualSourceId);
	if (!newVisualSourceNode) {
		return { nodes, edges, success: false, message: 'New visual source not found' };
	}

	// Find the default segment for this switch
	const defaultSegment = allSegments.find(n => (n.data as SegmentNodeData).isDefault);
	if (!defaultSegment) {
		return { nodes, edges, success: false, message: 'Default segment not found' };
	}

	// Find the last step under the default segment (or the segment itself if empty)
	const lastStepInDefault = findLastStepInSegment(defaultSegment.id, nodes, edges);

	// Build new edges:
	// 1. Remove edge from newVisualSource to visualSource
	// 2. Remove edges from visualSource to all segments
	// 3. Add edges from newVisualSource to all segments
	// 4. Add edge from lastStepInDefault to visualSource (or from default segment if empty)
	const newEdges = edges.filter(e => {
		// Remove edge from newVisualSource to visualSource
		if (e.source === newVisualSourceId && e.target === visualSourceId) {
			return false;
		}
		// Remove edges from visualSource to segments
		if (e.source === visualSourceId && allSegments.some(s => s.id === e.target)) {
			return false;
		}
		return true;
	});

	// Add edges from newVisualSource to all segments (preserve sourceHandle/data)
	for (const segment of allSegments) {
		const oldEdge = edges.find(e => e.source === visualSourceId && e.target === segment.id);
		newEdges.push({
			id: `edge-${newVisualSourceId}-${segment.id}`,
			source: newVisualSourceId,
			target: segment.id,
			sourceHandle: oldEdge?.sourceHandle,
			type: oldEdge?.type || 'default',
			data: oldEdge?.data,
		});
	}

	// Add edge from lastStepInDefault to visualSource
	newEdges.push({
		id: `edge-${lastStepInDefault}-${visualSourceId}`,
		source: lastStepInDefault,
		target: visualSourceId,
		type: 'default',
	});

	// Re-apply layout
	const layoutResult = applyLayout(nodes, newEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		success: true,
	};
}

/**
 * Find the last step in a segment's chain.
 * If the segment has no steps, returns the segment ID itself.
 *
 * @param segmentId - ID of the segment node
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @returns ID of the last step in the segment, or the segment ID if empty
 */
function findLastStepInSegment(segmentId: string, nodes: Node<BaseNodeData>[], edges: Edge[]): string {
	let currentId = segmentId;
	const maxIterations = 1000;

	for (let i = 0; i < maxIterations; i++) {
		const children = getChildNodeIds(currentId, edges);

		// No children means we're at the end
		if (children.length === 0) {
			return currentId;
		}

		// Find the first child that is a step (not segment/trigger)
		const stepChild = children.find(childId => {
			const childNode = nodes.find(n => n.id === childId);
			return childNode && childNode.type !== NODE_TYPES.SEGMENT && childNode.type !== NODE_TYPES.TRIGGER;
		});

		if (!stepChild) {
			// No step children (might be segments branching off), return current
			return currentId;
		}

		currentId = stepChild;
	}

	// Fallback: return current node if max iterations reached
	return currentId;
}

/**
 * Move a step down by swapping with its successor.
 *
 * The step takes the successor's position in the flow, and the successor
 * becomes the step's predecessor.
 *
 * To swap A -> B (where we want B to end up before A), we move B to be before A.
 * This avoids the cycle detection issue that would occur if we tried to move A after B.
 *
 * Special case: If moving a segment, pops the first step from the default segment
 * to become the new visual source above the switch.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param nodeId - ID of the node to move down
 * @returns MoveNodeResult with updated nodes and edges
 */
export function moveStepDown(nodes: Node<BaseNodeData>[], edges: Edge[], nodeId: string): MoveNodeResult {
	if (!canMoveStepDown(nodeId, nodes, edges)) {
		return { nodes, edges, success: false, message: 'Cannot move step down' };
	}

	const node = nodes.find(n => n.id === nodeId);
	if (!node) {
		return { nodes, edges, success: false, message: 'Node not found' };
	}

	// Special case: Moving a segment moves the entire switch structure down
	if (node.type === NODE_TYPES.SEGMENT) {
		return moveSegmentDown(nodes, edges, nodeId);
	}

	const childIds = getChildNodeIds(nodeId, edges);
	if (childIds.length !== 1) {
		return { nodes, edges, success: false, message: 'No single successor found' };
	}

	const successorId = childIds[0];

	// To swap, move the successor to be before the node.
	// This is equivalent to moving the node down, but avoids cycle detection issues.
	return moveNodeBefore(nodes, edges, successorId, nodeId);
}

/**
 * Move a segment (and its entire switch structure) down by one position.
 *
 * This works by popping the first step from the default segment to become
 * the new visual source (parent step) above the switch.
 *
 * @param nodes - Current nodes
 * @param edges - Current edges
 * @param segmentId - ID of the segment node
 * @returns MoveNodeResult with updated nodes and edges
 */
function moveSegmentDown(nodes: Node<BaseNodeData>[], edges: Edge[], segmentId: string): MoveNodeResult {
	const segmentNode = nodes.find(n => n.id === segmentId);
	if (!segmentNode || segmentNode.type !== NODE_TYPES.SEGMENT) {
		return { nodes, edges, success: false, message: 'Segment not found' };
	}

	const segmentData = segmentNode.data as SegmentNodeData;

	// Find all segments for this switch
	const allSegments = nodes.filter(
		n =>
			n.type === NODE_TYPES.SEGMENT && (n.data as SegmentNodeData).parentSwitchNodeId === segmentData.parentSwitchNodeId
	);

	// Find the current visual source (what connects to segments)
	const incomingEdge = edges.find(e => e.target === segmentId);
	if (!incomingEdge) {
		return { nodes, edges, success: false, message: 'No incoming edge found' };
	}

	const currentVisualSourceId = incomingEdge.source;

	// Find the default segment
	const defaultSegment = allSegments.find(n => (n.data as SegmentNodeData).isDefault);
	if (!defaultSegment) {
		return { nodes, edges, success: false, message: 'Default segment not found' };
	}

	// Find the first step under the default segment
	const defaultChildren = getChildNodeIds(defaultSegment.id, edges);
	if (defaultChildren.length === 0) {
		return { nodes, edges, success: false, message: 'No step under default segment' };
	}

	const firstStepUnderDefaultId = defaultChildren[0];
	const firstStepUnderDefault = nodes.find(n => n.id === firstStepUnderDefaultId);
	if (!firstStepUnderDefault) {
		return { nodes, edges, success: false, message: 'First step under default not found' };
	}

	// Find what's after the first step (if anything) - this will become new first child of default
	const firstStepChildren = getChildNodeIds(firstStepUnderDefaultId, edges);

	// Build new edges:
	// 1. Remove edge from currentVisualSource to all segments
	// 2. Remove edge from defaultSegment to firstStep
	// 3. Add edge from currentVisualSource to firstStep (firstStep becomes new visual source's child)
	// 4. Add edge from firstStep to all segments (firstStep becomes new visual source for switch)
	// 5. If firstStep had children, connect defaultSegment to firstStep's first child
	const newEdges = edges.filter(e => {
		// Remove edges from currentVisualSource to segments
		if (e.source === currentVisualSourceId && allSegments.some(s => s.id === e.target)) {
			return false;
		}
		// Remove edge from defaultSegment to firstStep
		if (e.source === defaultSegment.id && e.target === firstStepUnderDefaultId) {
			return false;
		}
		// Remove edge from firstStep to its children (will reconnect to default)
		if (e.source === firstStepUnderDefaultId && firstStepChildren.includes(e.target)) {
			return false;
		}
		return true;
	});

	// Add edge from currentVisualSource to firstStep
	newEdges.push({
		id: `edge-${currentVisualSourceId}-${firstStepUnderDefaultId}`,
		source: currentVisualSourceId,
		target: firstStepUnderDefaultId,
		type: 'default',
	});

	// Add edges from firstStep to all segments (preserve sourceHandle/data from old edges)
	for (const segment of allSegments) {
		const oldEdge = edges.find(e => e.source === currentVisualSourceId && e.target === segment.id);
		newEdges.push({
			id: `edge-${firstStepUnderDefaultId}-${segment.id}`,
			source: firstStepUnderDefaultId,
			target: segment.id,
			sourceHandle: oldEdge?.sourceHandle,
			type: oldEdge?.type || 'default',
			data: oldEdge?.data,
		});
	}

	// If firstStep had children, connect defaultSegment to the first child
	if (firstStepChildren.length > 0) {
		newEdges.push({
			id: `edge-${defaultSegment.id}-${firstStepChildren[0]}`,
			source: defaultSegment.id,
			target: firstStepChildren[0],
			type: 'default',
		});
	}

	// Re-apply layout
	const layoutResult = applyLayout(nodes, newEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		success: true,
	};
}

/**
 * Determine the insertion point based on drop position.
 * This analyzes where a node should be inserted based on canvas coordinates.
 *
 * @param position - Drop position in flow coordinates
 * @param nodes - Current nodes
 * @param excludeNodeIds - Node IDs to exclude (being dragged)
 */
export function getInsertionPoint(
	position: { x: number; y: number },
	nodes: Node<BaseNodeData>[],
	excludeNodeIds: string[] = []
): InsertionPoint {
	// Find the closest node to the drop position
	let closestNode: Node<BaseNodeData> | null = null;
	let closestDistance = Infinity;

	for (const node of nodes) {
		if (excludeNodeIds.includes(node.id)) {
			continue;
		}

		// Calculate center of node
		const nodeWidth = node.measured?.width ?? 560;
		const nodeHeight = node.measured?.height ?? 100;
		const nodeCenterX = node.position.x + nodeWidth / 2;
		const nodeCenterY = node.position.y + nodeHeight / 2;

		const distance = Math.sqrt(Math.pow(position.x - nodeCenterX, 2) + Math.pow(position.y - nodeCenterY, 2));

		if (distance < closestDistance) {
			closestDistance = distance;
			closestNode = node;
		}
	}

	if (!closestNode || closestDistance > 500) {
		return { type: 'invalid', targetNodeId: null };
	}

	// Determine if drop is before or after based on Y position
	const closestNodeCenterY = closestNode.position.y + (closestNode.measured?.height ?? 100) / 2;
	const type = position.y < closestNodeCenterY ? 'before' : 'after';

	return {
		type,
		targetNodeId: closestNode.id,
	};
}
